import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Search, Check, X, ExternalLink, Pencil } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '../../lib/api';
import { toast } from '../../hooks/use-toast';
import { Sheet, SheetContent } from '../../components/ui/sheet';

const usd = (v) => `$${Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const iso = (d) => d.toISOString().slice(0, 10);
const shortDate = (s) => {
  if (!s) return '—';
  const [y, m, d] = s.split('-').map(Number);
  const dt = new Date(y, (m || 1) - 1, d || 1);
  const txt = dt.toLocaleDateString('en-US', { month: 'short', day: '2-digit' });
  return dt.getFullYear() === new Date().getFullYear() ? txt : `${txt}, ${dt.getFullYear()}`;
};
const longDate = (s) => (s ? new Date(`${s}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }) : '—');

const ranges = () => {
  const t = new Date();
  const y = t.getFullYear();
  const m = t.getMonth();
  const dow = (t.getDay() + 6) % 7;
  return {
    today: { from: iso(t), to: iso(t) },
    week: { from: iso(new Date(y, m, t.getDate() - dow)), to: iso(t) },
    month: { from: iso(new Date(y, m, 1)), to: iso(t) },
    last: { from: iso(new Date(y, m - 1, 1)), to: iso(new Date(y, m, 0)) },
    custom: null,
  };
};
const DATE_TABS = [['today', 'Today'], ['week', 'This Week'], ['month', 'This Month'], ['last', 'Last Month'], ['custom', 'Custom']];

export const Commissions = () => {
  const [preset, setPreset] = useState('month');
  const [range, setRange] = useState(ranges().month);
  const [salesperson, setSalesperson] = useState('');
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [view, setView] = useState('all');
  const [data, setData] = useState({ rows: [], by_salesperson: [], salespeople: [], summary: {}, can_edit: false });
  const [sel, setSel] = useState([]);
  const [payDate, setPayDate] = useState(iso(new Date()));
  const [detail, setDetail] = useState(null);
  const [editing, setEditing] = useState('');
  const [editVal, setEditVal] = useState('');
  const [defOpen, setDefOpen] = useState(false);
  const [defVal, setDefVal] = useState('');
  const [busy, setBusy] = useState(false);
  const searchTimer = useRef(null);

  const load = useCallback(async (override) => {
    const r = override?.range ?? range;
    try {
      const { data: d } = await api.get('/finance/commissions', {
        params: { date_from: r?.from || '', date_to: r?.to || '',
          salesperson: override?.salesperson ?? salesperson, status: override?.status ?? status, q: override?.q ?? q },
      });
      setData(d);
      setSel((s) => s.filter((id) => d.rows.some((r2) => r2.deal_id === id)));
    } catch (e) { toast({ title: 'Erro ao carregar comissões', description: e.response?.data?.detail || '' }); }
  }, [range, salesperson, status, q]);

  useEffect(() => { load(); }, [range, salesperson, status]); // eslint-disable-line
  useEffect(() => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => load(), 260);
    return () => clearTimeout(searchTimer.current);
  }, [q]); // eslint-disable-line

  const pickPreset = (k) => {
    setPreset(k);
    if (k !== 'custom') setRange(ranges()[k]);
  };

  const rows = data.rows || [];
  const s = data.summary || {};
  const canEdit = !!data.can_edit;
  const selTotal = useMemo(() => rows.filter((r) => sel.includes(r.deal_id)).reduce((a, r) => a + r.amount, 0), [rows, sel]);
  const allSel = rows.length > 0 && sel.length === rows.length;

  const saveAmount = async (r) => {
    const amount = Number(String(editVal).replace(/[^0-9.]/g, ''));
    setEditing('');
    if (!Number.isFinite(amount) || amount === r.amount) return;
    try {
      await api.put(`/finance/commissions/${r.deal_id}`, { amount });
      await load();
    } catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
  };

  const markPaid = async (ids) => {
    if (!ids.length) return;
    setBusy(true);
    try {
      const { data: d } = await api.post('/finance/commissions/pay', { deal_ids: ids, payment_date: payDate });
      toast({ title: `${d.paid} comissão(ões) marcada(s) como paga`, description: `Pagamento em ${longDate(payDate)} por ${d.paid_by}` });
      setSel([]);
      await load();
      if (detail && ids.includes(detail.deal_id)) setDetail(null);
    } catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
    finally { setBusy(false); }
  };

  const reopen = async (id) => {
    try { await api.post('/finance/commissions/unpay', { deal_ids: [id] }); await load(); setDetail(null); }
    catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
  };

  const saveDefault = async (applyPending) => {
    const amount = Number(String(defVal).replace(/[^0-9.]/g, ''));
    if (!Number.isFinite(amount)) return;
    try {
      const { data: d } = await api.put('/finance/commissions/settings', { default_amount: amount, apply_to_pending: applyPending });
      toast({ title: `Comissão padrão: ${usd(d.default_amount)}`, description: applyPending ? `${d.applied_to_pending} pendentes atualizadas` : 'Vale para as próximas vendas' });
      setDefOpen(false);
      await load();
    } catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
  };

  return (
    <div data-testid="commissions-section">
      <div className="flex flex-wrap items-end justify-between gap-3 pb-3">
        <div>
          <h2 className="font-display text-[19px] font-bold tracking-tight">Comissões</h2>
          <p className="text-[11.5px] text-muted-foreground mt-0.5">Manage salesperson commissions and payment status</p>
        </div>
        {canEdit && (
          defOpen ? (
            <div className="flex items-end gap-2">
              <div><p className="ent-label mb-1">Comissão padrão</p>
                <input data-testid="cm-default-input" autoFocus value={defVal} onChange={(e) => setDefVal(e.target.value)} className="ent-input num" style={{ width: 110 }} /></div>
              <button data-testid="cm-default-save" onClick={() => saveDefault(false)} className="ent-btn h-8 px-3">Salvar</button>
              <button data-testid="cm-default-apply" onClick={() => saveDefault(true)} className="ent-btn ent-btn-primary h-8 px-3">Salvar + aplicar às pendentes</button>
              <button onClick={() => setDefOpen(false)} className="ent-btn ent-btn-icon"><X className="h-3.5 w-3.5" /></button>
            </div>
          ) : (
            <button data-testid="cm-default-open" onClick={() => { setDefVal(String(data.default_amount ?? 0)); setDefOpen(true); }}
              className="group flex items-center gap-1.5 text-[11.5px] text-muted-foreground hover:text-foreground transition-colors">
              <Pencil className="h-3 w-3" /> Comissão padrão por venda: <span className="num font-semibold text-foreground">{usd(data.default_amount)}</span>
            </button>
          )
        )}
      </div>

      <div className="cm-figures" data-testid="cm-figures">
        <div className="cm-figure"><p className="cm-figure-label">Total commissions</p><p className="cm-figure-value" data-testid="cm-total">{usd(s.total)}</p></div>
        <div className="cm-figure"><p className="cm-figure-label">Pending</p><p className="cm-figure-value t-warn" data-testid="cm-pending">{usd(s.pending)}</p><p className="text-[10px] cm-muted mt-0.5 num">{s.pending_count || 0} vendas</p></div>
        <div className="cm-figure"><p className="cm-figure-label">Paid</p><p className="cm-figure-value t-ok" data-testid="cm-paid">{usd(s.paid)}</p><p className="text-[10px] cm-muted mt-0.5 num">{s.paid_count || 0} vendas</p></div>
        <div className="cm-figure"><p className="cm-figure-label">Sales</p><p className="cm-figure-value" data-testid="cm-sales">{s.sales || 0}</p></div>
      </div>

      <div className="flex flex-wrap items-center gap-2 py-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <input data-testid="cm-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search commissions..."
            className="ent-input" style={{ width: 236, paddingLeft: 30 }} />
        </div>
        <div className="cm-seg" data-testid="cm-date-tabs">
          {DATE_TABS.map(([k, l]) => (
            <button key={k} data-testid={`cm-date-${k}`} data-on={preset === k ? '1' : '0'} onClick={() => pickPreset(k)}>{l}</button>
          ))}
        </div>
        {preset === 'custom' && (
          <div className="flex items-center gap-1.5">
            <input data-testid="cm-date-start" type="date" className="ent-input" value={range?.from || ''} onChange={(e) => setRange({ ...(range || {}), from: e.target.value })} />
            <span className="cm-muted text-xs">→</span>
            <input data-testid="cm-date-end" type="date" className="ent-input" value={range?.to || ''} onChange={(e) => setRange({ ...(range || {}), to: e.target.value })} />
          </div>
        )}
        {data.view_all && (
          <select data-testid="cm-salesperson" value={salesperson} onChange={(e) => setSalesperson(e.target.value)} className="ent-input" style={{ width: 186 }}>
            <option value="" className="bg-popover text-foreground">All Salespeople</option>
            {(data.salespeople || []).map((sp) => <option key={sp} value={sp} className="bg-popover text-foreground">{sp}</option>)}
          </select>
        )}
        <select data-testid="cm-status" value={status} onChange={(e) => setStatus(e.target.value)} className="ent-input" style={{ width: 130 }}>
          <option value="" className="bg-popover text-foreground">All Status</option>
          <option value="pending" className="bg-popover text-foreground">Pending</option>
          <option value="paid" className="bg-popover text-foreground">Paid</option>
        </select>
        <div className="cm-seg ml-auto" data-testid="cm-view-tabs">
          <button data-testid="cm-view-all" data-on={view === 'all' ? '1' : '0'} onClick={() => setView('all')}>All Commissions</button>
          <button data-testid="cm-view-sp" data-on={view === 'sp' ? '1' : '0'} onClick={() => setView('sp')}>By Salesperson</button>
        </div>
      </div>

      {view === 'sp' ? (
        <div className="border-t border-border" data-testid="cm-by-salesperson">
          {!data.by_salesperson?.length && <p className="ent-empty">Nenhuma comissão no período</p>}
          {(data.by_salesperson || []).map((r) => (
            <button key={r.salesperson} data-testid={`cm-sp-${r.salesperson}`}
              onClick={() => { setSalesperson(r.salesperson); setView('all'); }}
              className="flex w-full items-center gap-6 border-b border-border/50 px-1 py-3 text-left transition-colors hover:bg-foreground/[0.028]">
              <span className="min-w-[180px] text-[13.5px] font-semibold">{r.salesperson}</span>
              <span className="num text-[12px] cm-muted w-[74px]">{r.sales} sales</span>
              <span className="cm-money w-[112px] text-[14px]">{usd(r.total)}</span>
              <span className="num w-[112px] text-right text-[12.5px] t-ok">{usd(r.paid)} paid</span>
              <span className="num w-[124px] text-right text-[12.5px] t-warn">{usd(r.pending)} pending</span>
              <span className="ml-auto text-[11px] cm-muted">ver comissões →</span>
            </button>
          ))}
        </div>
      ) : (
        <div className="border-t border-border">
          <div className="overflow-x-auto" style={{ maxHeight: 'calc(100vh - 340px)' }}>
            <table className="cm-table" data-testid="cm-table">
              <thead>
                <tr>
                  {canEdit && <th style={{ width: 34 }}>
                    <input data-testid="cm-select-all" type="checkbox" checked={allSel} onChange={(e) => setSel(e.target.checked ? rows.map((r) => r.deal_id) : [])} />
                  </th>}
                  <th style={{ width: 84 }}>Date</th>
                  <th style={{ width: 78 }}>Deal</th>
                  <th>Vehicle</th>
                  <th>Customer</th>
                  <th>Salesperson</th>
                  <th className="cm-num" style={{ width: 118 }}>Commission</th>
                  <th style={{ width: 96 }}>Status</th>
                  <th style={{ width: 92 }}>Payment</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.deal_id} data-testid={`cm-row-${r.deal_id}`} data-sel={sel.includes(r.deal_id) ? '1' : '0'} onClick={() => setDetail(r)}>
                    {canEdit && (
                      <td onClick={(e) => e.stopPropagation()}>
                        <input data-testid={`cm-check-${r.deal_id}`} type="checkbox" checked={sel.includes(r.deal_id)}
                          onChange={(e) => setSel(e.target.checked ? [...sel, r.deal_id] : sel.filter((x) => x !== r.deal_id))} />
                      </td>
                    )}
                    <td className="num cm-muted">{shortDate(r.sale_date)}</td>
                    <td className="num">{r.deal_id}</td>
                    <td className="max-w-[260px] truncate font-medium">{r.vehicle || '—'}</td>
                    <td className="max-w-[180px] truncate">{r.customer || '—'}</td>
                    <td className="max-w-[150px] truncate">{r.salesperson}</td>
                    <td className="cm-money" onClick={(e) => { if (!canEdit) return; e.stopPropagation(); setEditing(r.deal_id); setEditVal(String(r.amount)); }}>
                      {editing === r.deal_id ? (
                        <input data-testid={`cm-amount-input-${r.deal_id}`} autoFocus className="cm-edit" value={editVal}
                          onChange={(e) => setEditVal(e.target.value)} onBlur={() => saveAmount(r)}
                          onKeyDown={(e) => { if (e.key === 'Enter') saveAmount(r); if (e.key === 'Escape') setEditing(''); }} />
                      ) : (
                        <span data-testid={`cm-amount-${r.deal_id}`} className={r.amount ? '' : 'cm-muted'}>{usd(r.amount)}</span>
                      )}
                    </td>
                    <td data-testid={`cm-status-${r.deal_id}`} className={r.status === 'paid' ? 't-ok' : 'cm-muted'}>
                      <span className="cm-dot" style={{ background: r.status === 'paid' ? 'hsl(var(--ok))' : 'hsl(var(--warn))' }} />
                      {r.status === 'paid' ? 'Paid' : 'Pending'}
                    </td>
                    <td className="num cm-muted">{r.payment_date ? shortDate(r.payment_date) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!rows.length && <p className="ent-empty">Nenhuma comissão encontrada com esses filtros</p>}
          </div>

          {canEdit && sel.length > 0 && (
            <div className="cm-actionbar" data-testid="cm-actionbar">
              <span className="text-[12.5px] font-semibold num" data-testid="cm-sel-count">{sel.length} selected</span>
              <span className="cm-muted text-[12.5px]">·</span>
              <span className="cm-money text-[14px]" data-testid="cm-sel-total">{usd(selTotal)} total</span>
              <div className="ml-auto flex items-center gap-2">
                <span className="ent-label">Payment date</span>
                <input data-testid="cm-pay-date" type="date" className="ent-input" value={payDate} onChange={(e) => setPayDate(e.target.value)} />
                <button data-testid="cm-clear-sel" onClick={() => setSel([])} className="ent-btn h-8 px-3">Cancelar</button>
                <button data-testid="cm-mark-paid" disabled={busy} onClick={() => markPaid(sel)} className="ent-btn ent-btn-primary h-8 px-4">
                  <Check className="h-3.5 w-3.5" /> Mark as Paid
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <Sheet open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <SheetContent side="right" className="w-full border-border bg-card text-foreground sm:max-w-[420px]" data-testid="cm-drawer">
          {detail && (
            <div className="pt-1">
              <p className="ent-label">Commission</p>
              <p className="mt-1 font-display text-[26px] font-bold tracking-tight num">{usd(detail.amount)}</p>
              <p className={`mt-1 text-[12px] ${detail.status === 'paid' ? 't-ok' : 't-warn'}`}>
                <span className="cm-dot" style={{ background: detail.status === 'paid' ? 'hsl(var(--ok))' : 'hsl(var(--warn))' }} />
                {detail.status === 'paid' ? `Paid · ${longDate(detail.payment_date)}` : 'Pending payment'}
              </p>

              <p className="cm-section">Sale</p>
              <div className="cm-drawer-row"><span className="cm-drawer-k">Deal #</span><span className="cm-drawer-v">{detail.deal_id}</span></div>
              <div className="cm-drawer-row"><span className="cm-drawer-k">Sale date</span><span className="cm-drawer-v">{longDate(detail.sale_date)}</span></div>
              <div className="cm-drawer-row"><span className="cm-drawer-k">Customer</span><span className="cm-drawer-v">{detail.customer || '—'}</span></div>
              <div className="cm-drawer-row"><span className="cm-drawer-k">Sale price</span><span className="cm-drawer-v">{usd(detail.sale_price)}</span></div>
              <div className="cm-drawer-row"><span className="cm-drawer-k">Payment type</span><span className="cm-drawer-v">{detail.finance_type || '—'}</span></div>

              <p className="cm-section">Vehicle</p>
              <div className="cm-drawer-row"><span className="cm-drawer-k">Year / Make / Model</span><span className="cm-drawer-v">{detail.vehicle || '—'}</span></div>
              <div className="cm-drawer-row"><span className="cm-drawer-k">VIN</span><span className="cm-drawer-v text-[11.5px]">{detail.vin || '—'}</span></div>
              <div className="cm-drawer-row"><span className="cm-drawer-k">Stock #</span><span className="cm-drawer-v">{detail.stock || '—'}</span></div>

              <p className="cm-section">Salesperson</p>
              <div className="cm-drawer-row"><span className="cm-drawer-k">Salesperson</span><span className="cm-drawer-v">{detail.salesperson}</span></div>

              {detail.status === 'paid' && (
                <>
                  <p className="cm-section">Payment</p>
                  <div className="cm-drawer-row"><span className="cm-drawer-k">Payment date</span><span className="cm-drawer-v">{longDate(detail.payment_date)}</span></div>
                  <div className="cm-drawer-row"><span className="cm-drawer-k">Paid by</span><span className="cm-drawer-v">{detail.paid_by || '—'}</span></div>
                  <div className="cm-drawer-row"><span className="cm-drawer-k">Registered at</span><span className="cm-drawer-v text-[11.5px]">{(detail.paid_at || '').slice(0, 16).replace('T', ' ')}</span></div>
                </>
              )}

              <div className="mt-6 flex flex-wrap items-center gap-2">
                <Link data-testid="cm-view-deal" to={`/app/deals?deal=${detail.deal_id}`} onClick={() => setDetail(null)} className="ent-btn h-8 px-3">
                  <ExternalLink className="h-3.5 w-3.5" /> View Original Deal
                </Link>
                {canEdit && detail.status !== 'paid' && (
                  <button data-testid="cm-drawer-pay" disabled={busy} onClick={() => markPaid([detail.deal_id])} className="ent-btn ent-btn-primary h-8 px-4">
                    <Check className="h-3.5 w-3.5" /> Mark as Paid
                  </button>
                )}
                {canEdit && detail.status === 'paid' && (
                  <button data-testid="cm-drawer-reopen" onClick={() => reopen(detail.deal_id)} className="ent-btn h-8 px-3">Reabrir pagamento</button>
                )}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};
